import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import { getOrCreateShortLink } from '../../services/memorialLinkService';

const {
  FiX, FiCopy, FiCheck, FiTwitter, FiFacebook, FiLinkedin,
  FiMail, FiMessageCircle, FiLink, FiZap, FiLoader, FiAlertCircle,
} = FiIcons;

/**
 * ShareModal
 *
 * Props:
 *   isOpen        {boolean}
 *   onClose       {function}
 *   title         {string}
 *   url           {string}   – raw fallback URL (non-memorial usage)
 *   memorialId    {string}   – triggers slug + short-link generation
 *   memorialName  {string}   – deceased's full name
 *   description   {string}
 */
const ShareModal = ({
  isOpen, onClose, title, url,
  memorialId, memorialName, description,
}) => {
  const [activeTab, setActiveTab]     = useState('slug');
  const [linkData, setLinkData]       = useState(null);
  const [loading, setLoading]         = useState(false);
  const [error, setError]             = useState(false);
  const [copiedSlug, setCopiedSlug]   = useState(false);
  const [copiedShort, setCopiedShort] = useState(false);

  const isMemorial = !!(memorialId && memorialName);

  /* ── Generate / fetch both URLs when modal opens ─────────────────── */
  const resolveLinks = useCallback(async () => {
    if (!isMemorial) return;
    setLoading(true);
    setError(false);
    try {
      const data = await getOrCreateShortLink(memorialId, memorialName);
      setLinkData(data);
      // Default to short tab if short URL was generated successfully
      if (data.shortUrl) setActiveTab('short');
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [memorialId, memorialName, isMemorial]);

  useEffect(() => {
    if (isOpen) {
      setActiveTab('slug');
      setCopiedSlug(false);
      setCopiedShort(false);
      setLinkData(null);
      resolveLinks();
    }
  }, [isOpen, resolveLinks]);

  if (!isOpen) return null;

  /* ── Resolved URLs ────────────────────────────────────────────────── */
  const slugUrl  = linkData?.slugUrl  || url || window.location.href;
  const shortUrl = linkData?.shortUrl || null;
  const hasShort = !!shortUrl;

  /* ── Active URL used by social share buttons ──────────────────────── */
  const activeUrl = (activeTab === 'short' && hasShort) ? shortUrl : slugUrl;

  /* ── Clipboard helper ─────────────────────────────────────────────── */
  const copyText = (text, setter) => {
    if (!text) return;
    navigator.clipboard.writeText(text).catch(() => {
      const ta = document.createElement('textarea');
      ta.value = text;
      Object.assign(ta.style, { position: 'fixed', opacity: '0' });
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
    });
    setter(true);
    setTimeout(() => setter(false), 2200);
  };

  /* ── Social platforms ─────────────────────────────────────────────── */
  const platforms = [
    {
      name:  'Twitter',
      icon:  FiTwitter,
      color: '#1DA1F2',
      href:  `https://twitter.com/intent/tweet?text=${encodeURIComponent(title)}&url=${encodeURIComponent(activeUrl)}`,
    },
    {
      name:  'Facebook',
      icon:  FiFacebook,
      color: '#1877F2',
      href:  `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(activeUrl)}`,
    },
    {
      name:  'WhatsApp',
      icon:  FiMessageCircle,
      color: '#25D366',
      href:  `https://wa.me/?text=${encodeURIComponent(`${title}\n${activeUrl}`)}`,
    },
    {
      name:  'LinkedIn',
      icon:  FiLinkedin,
      color: '#0077B5',
      href:  `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(activeUrl)}`,
    },
    {
      name:  'Email',
      icon:  FiMail,
      color: '#1B2B5E',
      href:  `mailto:?subject=${encodeURIComponent(title)}&body=${encodeURIComponent(`${description || title}\n\n${activeUrl}`)}`,
    },
  ];

  return (
    <div
      className="fixed inset-0 z-[350] flex items-center justify-center p-4"
      style={{ backgroundColor: 'rgba(27,43,94,0.65)', backdropFilter: 'blur(10px)' }}
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.94, y: 24 }}
        animate={{ opacity: 1, scale: 1,    y: 0  }}
        exit={{    opacity: 0, scale: 0.94, y: 24 }}
        transition={{ type: 'spring', stiffness: 340, damping: 28 }}
        className="bg-white rounded-[36px] shadow-2xl w-full max-w-md overflow-hidden"
      >
        {/* ── Header ──────────────────────────────────────────────── */}
        <div className="px-8 pt-8 pb-5">
          <div className="flex items-start justify-between">
            <div>
              <h2 className="text-2xl font-serif text-accent-navy leading-tight">Share Memory</h2>
              <p className="text-[10px] font-black uppercase tracking-[0.2em] text-text-muted mt-0.5">
                Help others honour this legacy
              </p>
            </div>
            <button
              onClick={onClose}
              className="p-2 rounded-full hover:bg-brand-light transition-colors mt-0.5"
              aria-label="Close"
            >
              <SafeIcon icon={FiX} className="w-5 h-5 text-text-muted" />
            </button>
          </div>

          {/* Memorial name banner */}
          {isMemorial && (
            <div className="mt-5 bg-brand-light border border-brand-dark rounded-2xl px-5 py-3.5">
              <p className="text-[9px] font-black uppercase tracking-[0.2em] text-text-muted mb-1">
                Sharing memorial for
              </p>
              <p className="font-serif text-accent-navy text-lg leading-snug">{memorialName}</p>

              {loading && (
                <div className="flex items-center gap-1.5 mt-2">
                  <SafeIcon icon={FiLoader} className="w-3 h-3 text-accent-gold animate-spin" />
                  <span className="text-[9px] text-text-muted font-bold">Generating personalised links…</span>
                </div>
              )}
              {!loading && hasShort && (
                <div className="flex items-center gap-1.5 mt-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-green-500 inline-block" />
                  <span className="text-[9px] text-green-600 font-bold">Short link ready</span>
                </div>
              )}
              {!loading && error && (
                <div className="flex items-center gap-1.5 mt-2">
                  <SafeIcon icon={FiAlertCircle} className="w-3 h-3 text-amber-500" />
                  <span className="text-[9px] text-amber-600 font-bold">Using name URL (short link unavailable)</span>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="px-8 pb-8 space-y-6">
          {/* ── Social share grid ───────────────────────────────────── */}
          <div className="grid grid-cols-5 gap-3">
            {platforms.map((p) => (
              <a
                key={p.name}
                href={p.href}
                target="_blank"
                rel="noopener noreferrer"
                className="flex flex-col items-center gap-2 group"
              >
                <div
                  className="w-11 h-11 rounded-2xl flex items-center justify-center text-white shadow-md group-hover:scale-110 group-hover:shadow-lg transition-all duration-200"
                  style={{ backgroundColor: p.color }}
                >
                  <SafeIcon icon={p.icon} className="w-5 h-5" />
                </div>
                <span className="text-[8px] font-black uppercase tracking-tight text-text-muted">
                  {p.name}
                </span>
              </a>
            ))}
          </div>

          {/* ── Link section ────────────────────────────────────────── */}
          <div className="border-t border-brand-light pt-5 space-y-4">

            {/* Tab switcher — only rendered when both URLs are available */}
            <AnimatePresence>
              {isMemorial && hasShort && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  className="overflow-hidden"
                >
                  <div className="flex gap-1.5 p-1 bg-brand-light rounded-xl border border-brand-dark mb-4">
                    <TabButton
                      active={activeTab === 'short'}
                      onClick={() => setActiveTab('short')}
                      icon={FiZap}
                      label="Short Link"
                    />
                    <TabButton
                      active={activeTab === 'slug'}
                      onClick={() => setActiveTab('slug')}
                      icon={FiLink}
                      label="Name URL"
                    />
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Short URL row */}
            <AnimatePresence mode="wait">
              {activeTab === 'short' && hasShort && (
                <motion.div
                  key="short"
                  initial={{ opacity: 0, y: 6 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -6 }}
                  transition={{ duration: 0.18 }}
                >
                  <UrlRow
                    label="Short Link"
                    hint={`Compact — links directly to ${memorialName?.split(' ')[0]}'s memorial`}
                    value={shortUrl}
                    copied={copiedShort}
                    disabled={false}
                    onCopy={() => copyText(shortUrl, setCopiedShort)}
                  />
                </motion.div>
              )}

              {/* Slug URL row */}
              {(activeTab === 'slug' || !hasShort) && (
                <motion.div
                  key="slug"
                  initial={{ opacity: 0, y: 6 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -6 }}
                  transition={{ duration: 0.18 }}
                >
                  <UrlRow
                    label={isMemorial ? 'Name-Based Link' : 'Direct Link'}
                    hint={
                      isMemorial
                        ? `Includes ${memorialName?.split(' ')[0]}'s name — easy to recognise`
                        : null
                    }
                    value={loading ? '' : slugUrl}
                    placeholder={loading ? 'Generating link…' : ''}
                    copied={copiedSlug}
                    disabled={loading}
                    onCopy={() => copyText(slugUrl, setCopiedSlug)}
                  />
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>

        {/* ── Footer ──────────────────────────────────────────────── */}
        <div className="bg-brand-light border-t border-brand-dark px-8 py-4 text-center">
          <p className="text-[9px] font-black uppercase tracking-[0.22em] text-text-muted">
            Sharing brings the community closer together
          </p>
        </div>
      </motion.div>
    </div>
  );
};

/* ── Sub-components ─────────────────────────────────────────────────────── */

const TabButton = ({ active, onClick, icon, label }) => (
  <button
    onClick={onClick}
    className={`flex-1 flex items-center justify-center gap-1.5 py-2 px-3 rounded-lg text-[9px] font-black uppercase tracking-widest transition-all ${
      active
        ? 'bg-white shadow text-accent-navy border border-brand-dark'
        : 'text-text-muted hover:text-accent-navy'
    }`}
  >
    <SafeIcon icon={icon} className="w-3 h-3" />
    {label}
  </button>
);

const UrlRow = ({ label, hint, value, placeholder, copied, disabled, onCopy }) => (
  <div>
    <p className="text-[10px] font-black uppercase tracking-widest text-accent-navy mb-1">{label}</p>
    {hint && <p className="text-[9px] text-text-muted italic mb-2">{hint}</p>}
    <div className="flex gap-2">
      <input
        readOnly
        value={value ?? ''}
        placeholder={placeholder ?? ''}
        className="flex-1 min-w-0 bg-brand-light border-2 border-brand-dark rounded-xl px-4 py-3 text-xs font-medium text-text-muted outline-none truncate"
      />
      <button
        onClick={onCopy}
        disabled={disabled || !value}
        className={`shrink-0 px-5 py-3 rounded-xl text-sm font-bold flex items-center gap-2 transition-all disabled:opacity-40 disabled:cursor-not-allowed ${
          copied
            ? 'bg-green-500 text-white'
            : 'bg-accent-navy text-white hover:bg-accent-navy/90 active:scale-95'
        }`}
      >
        <SafeIcon icon={copied ? FiCheck : FiCopy} className="w-4 h-4" />
        {copied ? 'Copied!' : 'Copy'}
      </button>
    </div>
  </div>
);

export default ShareModal;